import { Clock, Sun, Users, Calendar, Info } from "lucide-react";

// Days of the week
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Parse time string like "9am-12pm, 2pm-5pm" into an array
const parseTimeRanges = (timeStr) => {
  if (!timeStr) return [];
  return timeStr.split(",").map(t => t.trim()).filter(Boolean);
};

export default function BestTimesCalendar({ bestTimes, openingHours, isPremium }) {
  // Only show for premium venues (except opening hours which is always shown)
  if (!isPremium && !openingHours) return null;
  
  // For non-premium, only show opening hours
  if (!isPremium) {
    if (!openingHours) return null;
    return (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-brand" />
          Opening Hours
        </h3>
        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
          <Clock className="w-5 h-5 text-gray-500 mt-0.5" />
          <div>
            <p className="text-sm text-gray-600">{openingHours}</p>
          </div>
        </div>
      </div>
    );
  }

  const quietHours = parseTimeRanges(bestTimes?.quiet_hours);
  const peakHours = parseTimeRanges(bestTimes?.peak_hours);
  const busiestDay = bestTimes?.busiest_day;

  // If no meaningful data, don't render
  if (quietHours.length === 0 && peakHours.length === 0 && !busiestDay && !openingHours) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-brand" />
        Best Times to Work
      </h3>

      <div className="space-y-4">
        {/* Opening Hours */}
        {openingHours && (
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
            <Clock className="w-5 h-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900">Opening Hours</p>
              <p className="text-sm text-gray-600">{openingHours}</p>
            </div>
          </div>
        )}

        {/* Quiet Hours - Best for Work */}
        {quietHours.length > 0 && (
          <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
            <Sun className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-800">Quietest Times</p>
              <p className="text-sm text-green-700">Best for focused work</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {quietHours.map((time) => (
                  <span key={time} className="px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-medium">
                    {time}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Peak Hours - Avoid if possible */}
        {peakHours.length > 0 && (
          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
            <Users className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Busiest Times</p>
              <p className="text-sm text-amber-700">May be crowded & noisy</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {peakHours.map((time) => (
                  <span key={time} className="px-2 py-1 bg-amber-100 text-amber-700 rounded-md text-xs font-medium">
                    {time}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Busiest Day */}
        {busiestDay && (
          <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-100">
            <Info className="w-4 h-4 text-red-500" />
            <p className="text-sm text-red-700">
              <span className="font-medium">{busiestDay}</span> is typically the busiest day
            </p>
          </div>
        )}

        {/* Weekly Overview */}
        {busiestDay && (
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-2">Weekly Activity</p>
            <div className="grid grid-cols-7 gap-1">
              {DAYS.map((day) => {
                const isBusiest = day === busiestDay;
                const isWeekend = day === "Saturday" || day === "Sunday";
                return (
                  <div
                    key={day}
                    className={`text-center py-2 rounded-lg text-xs ${
                      isBusiest
                        ? "bg-red-100 text-red-700 font-medium"
                        : isWeekend
                        ? "bg-amber-50 text-amber-600"
                        : "bg-gray-50 text-gray-600"
                    }`}
                    title={isBusiest ? "Busiest day" : day}
                  >
                    {day.slice(0, 2)}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-100 rounded"></span>
                Busiest
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-amber-50 rounded"></span>
                Weekend
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-gray-50 rounded"></span>
                Normal
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tip */}
      <div className="mt-4 p-3 bg-brand/5 rounded-xl">
        <p className="text-xs text-brand">
          <span className="font-medium">Pro tip:</span> Arrive early during quiet hours for the best seats with power outlets!
        </p>
      </div>
    </div>
  );
}
